import React from "react";
import { motion } from "framer-motion";
import {
    FileText,
    Sofa,
    MonitorSmartphone,
    Camera,
    MessagesSquare,
    KeyRound,
    Receipt,
    Sparkles,
} from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/Reveal";
import { IMAGES } from "@/config";
import { easeLux, staggerParent, fadeUp, viewportOnce } from "@/lib/anim";

const SERVICES = [
    {
        icon: FileText,
        n: "01",
        title: "Avvio & burocrazia",
        desc: "CIR / CIN, comunicazioni alla questura, SCIA e adempimenti iniziali. Partiamo in regola, senza intoppi.",
    },
    {
        icon: Sofa,
        n: "02",
        title: "Allestimento & styling",
        desc: "Progettiamo e allestiamo la casa perché sia accogliente, fotogenica e pronta ad ospitare.",
    },
    {
        icon: MonitorSmartphone,
        n: "03",
        title: "Annunci & piattaforme",
        desc: "Set up e ottimizzazione su Airbnb, Booking e non solo: pricing dinamico e visibilità massima.",
    },
    {
        icon: Camera,
        n: "04",
        title: "Servizio fotografico",
        desc: "Fotografie professionali che valorizzano gli spazi e aumentano le prenotazioni.",
    },
    {
        icon: MessagesSquare,
        n: "05",
        title: "Gestione ospiti",
        desc: "Messaggistica pre, durante e post soggiorno. Risposte rapide e assistenza continua.",
    },
    {
        icon: KeyRound,
        n: "06",
        title: "Check-in & check-out",
        desc: "Accoglienza degli ospiti curata in ogni fase, con procedure sicure e affidabili.",
    },
    {
        icon: Receipt,
        n: "07",
        title: "Fisco & comunicazioni",
        desc: "Dichiarazioni fiscali e comunicazioni periodiche obbligatorie, gestite per te.",
    },
];

const ServiceCard = ({ s }) => {
    const Icon = s.icon;
    return (
        <motion.article
            variants={fadeUp}
            data-testid={`service-card-${s.n}`}
            className="group relative flex flex-col justify-between rounded-[2px] border border-[var(--fama-border)] bg-[var(--fama-surface)] p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_18px_40px_-24px_rgba(21,21,21,0.25)]"
        >
            <div>
                <div className="flex items-start justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--fama-gold-soft)] text-[var(--fama-gold)] transition-colors duration-500 group-hover:bg-[var(--fama-gold)] group-hover:text-white">
                        <Icon size={22} strokeWidth={1.4} />
                    </span>
                    <span className="font-serif-display text-3xl text-[var(--fama-border)] transition-colors duration-500 group-hover:text-[var(--fama-gold)]">
                        {s.n}
                    </span>
                </div>
                <h3 className="mt-6 font-serif-display text-2xl leading-tight text-[var(--fama-ink)]">
                    {s.title}
                </h3>
                <p className="mt-3 font-body text-sm leading-relaxed text-[var(--fama-ink-soft)]">
                    {s.desc}
                </p>
            </div>
        </motion.article>
    );
};

export default function Services() {
    return (
        <section
            id="servizi"
            className="relative scroll-mt-24 bg-[var(--fama-bg)] py-24 lg:py-32"
            data-testid="services-section"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="max-w-3xl">
                    <FadeUp>
                        <span className="font-body text-xs font-semibold uppercase tracking-[0.28em] text-[var(--fama-gold)]">
                            Servizi · Un percorso, non una lista
                        </span>
                    </FadeUp>
                    <MaskedLines
                        as="h2"
                        lines={["Un unico partner,", "dalle chiavi ai rendimenti."]}
                        className="mt-5 font-serif-display text-4xl leading-[1.02] tracking-[-0.01em] text-[var(--fama-ink)] sm:text-5xl lg:text-6xl"
                    />
                    <FadeUp delay={0.1}>
                        <p className="mt-6 max-w-2xl font-body text-base leading-relaxed text-[var(--fama-ink-soft)] sm:text-lg">
                            Ci occupiamo di tutto, in modo coordinato: ogni fase
                            si incastra nella successiva per un servizio davvero
                            “chiavi in mano”.
                        </p>
                    </FadeUp>
                </div>

                <motion.div
                    variants={staggerParent(0.08)}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
                >
                    {/* FaMa Lab highlight — spans 2 columns and 2 rows */}
                    <motion.article
                        variants={fadeUp}
                        data-testid="service-card-famalab"
                        className="group relative overflow-hidden rounded-[2px] border border-[var(--fama-gold)] bg-[var(--fama-ink)] p-8 sm:col-span-2 sm:row-span-2"
                    >
                        <div className="absolute inset-0 opacity-25 transition-opacity duration-700 group-hover:opacity-35">
                            <img
                                src={IMAGES.famaLab}
                                alt="Biancheria premium e ambienti curati da FaMa Lab"
                                className="h-full w-full object-cover"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[var(--fama-ink)] via-[var(--fama-ink)]/70 to-transparent" />
                        </div>
                        <div className="relative flex h-full min-h-[320px] flex-col justify-between">
                            <div className="flex items-center justify-between">
                                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--fama-gold)] text-[var(--fama-ink)]">
                                    <Sparkles size={22} strokeWidth={1.6} />
                                </span>
                                <span className="rounded-full border border-[var(--fama-gold)] px-4 py-1.5 font-body text-xs font-semibold uppercase tracking-[0.2em] text-[var(--fama-gold)]">
                                    Brand interno
                                </span>
                            </div>
                            <div className="mt-8">
                                <h3 className="font-serif-display text-4xl leading-none text-[#fdfbf7] sm:text-5xl">
                                    Pulizie & biancheria
                                    <span className="text-[var(--fama-gold)]">
                                        {" "}
                                        FaMa Lab
                                    </span>
                                </h3>
                                <p className="mt-5 max-w-lg font-body text-base leading-relaxed text-[#fdfbf7]/80">
                                    Pulizie e biancheria certificate FaMa Lab,
                                    il nostro brand interno dedicato alla cura
                                    degli ambienti. Il controllo qualità è
                                    interno all'azienda, mai affidato a terzi:
                                    ogni soggiorno inizia con standard
                                    impeccabili.
                                </p>
                            </div>
                        </div>
                    </motion.article>

                    {SERVICES.map((s) => (
                        <ServiceCard key={s.n} s={s} />
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
